import React, { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { string, number, object } from 'yup'
import { Trans, useTranslation } from 'react-i18next'
import { StyleSheet, ViewProps } from 'react-native'
import { Draft, useBuilding, ZoneData } from 'zens-store'
import { Form, NavigationConfirm, BuildingSelect, FormProps } from '@dms/component'
import { Color, Spacing, Text } from 'zens-storybook'

const styles = StyleSheet.create({
  location: {
    zIndex: 40,
  },
  building: {
    zIndex: 30,
  },
  description: {
    zIndex: 10,
  },
  descriptionInput: {
    flex: 1,
    maxWidth: '100%',
  },
  optional: {
    color: Color.calmGrey,
  },
  help: {
    color: Color.metalGrey,
    marginTop: Spacing.XS,
    marginBottom: Spacing.XS,
  },
})

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  data: ZoneData
  onSave?: (draft: Draft<ZoneData>) => Promise<void>
}
const ZoneAddForm = (props: Props) => {
  const { data, onSave, ...otherProps } = props
  const { t } = useTranslation()

  const draft = useRef(new Draft<ZoneData>(data)).current
  const { company_id: companyId, location_id: locationId, building_id: buildingId = 0 } = draft.data
  const building = useBuilding({
    companyId,
    buildingId,
  })

  useEffect(() => {
    Object.keys(data).forEach((key) => draft.update(key as keyof ZoneData, data[key as keyof ZoneData]))
  }, [data])

  useEffect(() => {
    if (building.data?.locationId && building.data?.locationId !== locationId) {
      draft.update('location_id', building.data?.locationId)
    }
  }, [building.data?.locationId])

  useEffect(() => {
    if (building.data?.locationId && building.data?.locationId !== locationId) {
      draft.update('building_id', undefined)
    }
  }, [locationId])

  const config = {
    fields: [
      {
        key: 'name',
        label: t('Zone name'),
      },
      {
        key: 'building_id',
        label: t('Select building'),
        FieldInputComponent: BuildingSelect,
        fieldProps: { companyId, groupLocation: true },
        style: styles.building,
      },
      {
        key: 'description',
        label: (
          <>
            <Text level={5}>
              <Trans>Description</Trans> <Text style={styles.optional}>({t('optional')})</Text>
            </Text>
            <Text level={5} style={styles.help}>
              <Trans>
                Describe the intended use for this zone. For example: used as dashboard filter, settings etcetera.
              </Trans>
            </Text>
          </>
        ),
        fieldProps: {
          multiline: true,
          numberOfLines: 3,
          style: styles.descriptionInput,
          maxLength: 256,
        },
        style: styles.description,
      },
    ],
  }
  const schema = object({
    name: string().required(t('Zone name is required')),
    location_id: number()
      .transform((value) => {
        return isNaN(value) ? undefined : value
      })
      .required(t('Location is required')),
    building_id: number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required(t('Building is required')),
  })
  return (
    <>
      <Form
        draft={draft}
        config={config}
        schema={schema}
        buttons={[
          [
            {
              title: t('Create zone'),
              onPress: onSave,
              validate: true,
            },
            {
              title: t('Cancel'),
              href: `/companies/${companyId}/workspaces/zones`,
              light: true,
            },
          ],
        ]}
        {...otherProps}
      />
      <NavigationConfirm draft={draft} />
    </>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()
  const config = {
    fields: [
      {
        key: 'name',
        label: t('Zone name'),
      },
      {
        key: 'location_id',
        label: t('Select location'),
        style: styles.location,
      },
      {
        key: 'building_id',
        label: t('Select building'),
        style: styles.building,
      },
      {
        key: 'description',
        label: t('Description'),
        optional: true,
        fieldProps: {
          multiline: true,
          style: styles.descriptionInput,
        },
        style: styles.description,
      },
    ],
  }
  return <Form.Skeleton config={config} numberOfbuttons={2} {...props} />
}
ZoneAddForm.Skeleton = Skeleton

export default observer(ZoneAddForm)
